<script setup lang="ts">
import { inject, ref, computed, watch, type Ref } from 'vue'
import formatAddress from '@/components/FormSection/formatAddress'
import formatPostcode from '@/components/FormSection/formatPostcode'
import addressLookup from '@/components/FormSection/addressLookup'
import FormLabel from '@/components/FormSection/FormLabel.vue'
import FormFieldGroup from '@/components/FormSection/FormFieldGroup.vue'
import FormRequiredSymbol from '@/components/FormSection/FormRequiredSymbol.vue'
import FormFieldError from '@/components/FormSection/FormFieldError.vue'
import { resetExternalError } from '@/components/FormSection/resetExternalError'
import {
  emptyQueryObj,
  hasAddress,
} from '@/components/FormSection/urlParamsHelperDonorDetails'
import {
  type UrlParamsDonorDetails,
  type DonorDetails,
  GIBRALTAR_POSTCODE,
} from '@/types'

const {
  formState,
  v$,
  hasSelectedAddress,
  DEFAULT_COUNTRY,
  $externalResults,
}: any = inject('formState')

const addressLookupError = ref(false)
const findingAddresses = ref(false)
const addressList: Ref<any[]> = ref([])
const selectedAddress = ref({})
const showAddressForm = ref(false)

const isUk = computed(() => {
  return formState.country === DEFAULT_COUNTRY
})

const findAddressButtonEnabled = computed(() => {
  return isUk.value && formState.postcode.length >= 4
})

const showSelectAddress = computed(() => {
  if (addressLookupError.value) return false

  return isUk.value && addressList.value.length > 0
})

const addressCount = computed(() => {
  const pluralised =
    addressList.value.length === 1 ? 'address found' : 'addresses found'
  return `${addressList.value.length} ${pluralised}`
})

const findAddress = async () => {
  if (!formState.postcode) return

  if (
    formState.postcode.replace(/\s+/g, '').toUpperCase() === GIBRALTAR_POSTCODE
  ) {
    formState.country = 'Gibraltar'
    formState.postcode = formatPostcode(GIBRALTAR_POSTCODE)
    enterAddressManually()
    return
  }

  addressLookupError.value = false
  findingAddresses.value = true
  const result = await addressLookup(formState.postcode)
  findingAddresses.value = false

  if (result.length === 0) {
    // no addresses found
    addressLookupError.value = true
    return
  }

  addressList.value = result
  return result
}

const enterAddressManually = () => {
  showAddressForm.value = true
  addressList.value = []
}

watch(hasSelectedAddress, () => {
  showAddressForm.value = true
})

watch(selectedAddress, (address: any) => {
  formState.addressLine1 = address.line_1
  formState.addressLine2 = address.line_2
  formState.city = address.post_town
  formState.county = address.county
})

watch(addressLookupError, (addressLookupError) => {
  if (addressLookupError) {
    showAddressForm.value = true
  }
})

watch(v$.value.addressLine1, (value) => {
  if (value.$invalid) {
    showAddressForm.value = true
  }
})

const handleResetExternalError = (fieldName: string) => {
  resetExternalError(fieldName, v$, $externalResults)
}

const urlParamsDonorDetails = inject<UrlParamsDonorDetails<DonorDetails>>(
  'urlParamsDonorDetails',
  emptyQueryObj
)
if (hasAddress(urlParamsDonorDetails)) {
  showAddressForm.value = true
}
</script>

<template>
  <form-field-group v-if="isUk" class="w-lg-75 mx-auto">
    <div class="row">
      <div class="col-12">
        <form-label for="postcode">
          {{ $t('form.formFields.postcode.label') }}
          <form-required-symbol />
        </form-label>
      </div>
    </div>
    <div class="row d-flex align-items-center">
      <div class="col-5">
        <div class="form-group postcode mb-0 position-relative">
          <input
            v-model="v$.postcode.$model"
            type="text"
            name="postcode"
            autocomplete="postal-code"
            class="form-control form-control-lg text-center text-uppercase"
            :class="{
              'is-valid': !v$.postcode.$error && v$.postcode.$dirty,
              'is-invalid': v$.postcode.$error && v$.postcode.$dirty,
            }"
            :placeholder="$t('form.formFields.postcode.placeholder')"
            data-testid="postcode-input"
            @blur="handleResetExternalError('postcode')"
          />
        </div>
      </div>
      <div class="col-7">
        <div
          :class="{ 'btn-disabled': !findAddressButtonEnabled }"
          class="form-group mb-0"
        >
          <button
            id="addressButtonSubmit"
            class="btn btn-primary btn-lg w-100"
            :disabled="!findAddressButtonEnabled"
            data-testid="find-address-button"
            @click.prevent="findAddress"
          >
            <div v-if="findingAddresses" role="status" aria-hidden="true">
              <span class="spinner-border spinner-border-sm"></span>
              <span class="small ms-3">Loading...</span>
            </div>
            <span v-else>
              {{ $t('form.formFields.postcode.button') }}
            </span>
          </button>
        </div>
      </div>
      <div class="col-12">
        <form-field-error
          v-if="v$.postcode.$error"
          :errors="v$.postcode.$errors"
        ></form-field-error>
      </div>
    </div>
    <div v-if="addressLookupError" class="row mt-3">
      <div class="col-12 text-center">
        {{ $t('form.formFields.postcode.noAddressFound') }}
      </div>
    </div>
  </form-field-group>

  <form-field-group v-if="showSelectAddress" class="w-lg-75 mx-auto">
    <form-label for="postcode">
      {{ $t('form.formFields.addressSelect.label') }}
    </form-label>
    <select
      v-model="selectedAddress"
      name="address"
      class="form-select form-select-lg"
      data-testid="address-select"
      @change="hasSelectedAddress = true"
    >
      <option :value="{}" disabled>
        {{ addressCount }}
      </option>
      <option
        v-for="address in addressList"
        :key="address.udprn"
        :value="address"
      >
        {{ formatAddress(address) }}
      </option>
    </select>

    <div class="mt-1 text-center small">
      <a
        id="enterAddressManually"
        href="#"
        @click.prevent="enterAddressManually"
      >
        {{ $t('form.formFields.addressSelect.help') }}
      </a>
    </div>
  </form-field-group>

  <div
    v-if="showAddressForm"
    data-testid="address-input-fields"
    class="w-lg-75 mx-auto"
  >
    <form-field-group>
      <form-label for="addressLine1">
        {{ $t('form.formFields.addressLine1.label') }}
        <form-required-symbol />
      </form-label>
      <input
        v-model="v$.addressLine1.$model"
        id="addressLine1"
        name="addressLine1"
        autocomplete="address-line1"
        class="form-control form-control-lg"
        :class="{
          'is-valid': !v$.addressLine1.$error && v$.addressLine1.$dirty,
          'is-invalid': v$.addressLine1.$error && v$.addressLine1.$dirty,
        }"
        :placeholder="$t('form.formFields.addressLine1.placeholder')"
        data-testid="addressline1-input"
        @blur="handleResetExternalError('addressLine1')"
      />
      <form-field-error
        v-if="v$.addressLine1.$error"
        :errors="v$.addressLine1.$errors"
      ></form-field-error>
    </form-field-group>

    <form-field-group>
      <form-label for="addressLine2">
        {{ $t('form.formFields.addressLine2.label') }}
      </form-label>
      <input
        v-model="v$.addressLine2.$model"
        id="addressLine2"
        name="addressLine2"
        autocomplete="address-line2"
        class="form-control form-control-lg"
        :class="{
          'is-valid': !v$.addressLine2.$error && v$.addressLine2.$dirty,
          'is-invalid': v$.addressLine2.$error && v$.addressLine2.$dirty,
        }"
        :placeholder="$t('form.formFields.addressLine2.placeholder')"
        data-testid="addressline2-input"
        @blur="handleResetExternalError('addressLine2')"
      />
      <form-field-error
        v-if="v$.addressLine2.$error"
        :errors="v$.addressLine2.$errors"
      ></form-field-error>
    </form-field-group>

    <form-field-group>
      <form-label for="city">
        {{ $t('form.formFields.city.label') }}
      </form-label>
      <input
        v-model="v$.city.$model"
        id="city"
        name="city"
        autocomplete="address-level2"
        class="form-control form-control-lg"
        :class="{
          'is-valid': !v$.city.$error && v$.city.$dirty,
          'is-invalid': v$.city.$error && v$.city.$dirty,
        }"
        :placeholder="$t('form.formFields.city.placeholder')"
        data-testid="city-input"
        @blur="handleResetExternalError('city')"
      />
      <form-field-error
        v-if="v$.city.$error"
        :errors="v$.city.$errors"
      ></form-field-error>
    </form-field-group>

    <form-field-group>
      <form-label for="county">
        {{
          isUk
            ? $t('form.formFields.county.label')
            : $t('form.formFields.state.label')
        }}
      </form-label>
      <input
        v-model="v$.county.$model"
        id="county"
        name="county"
        autocomplete="address-level1"
        class="form-control form-control-lg"
        :class="{
          'is-valid': !v$.county.$error && v$.county.$dirty,
          'is-invalid': v$.county.$error && v$.county.$dirty,
        }"
        :placeholder="$t('form.formFields.county.placeholder')"
        data-testid="county-input"
        @blur="handleResetExternalError('county')"
      />
      <form-field-error
        v-if="v$.county.$error"
        :errors="v$.county.$errors"
      ></form-field-error>
    </form-field-group>

    <form-field-group v-if="!isUk" class="mb-4">
      <form-label for="zipcode">
        {{ $t('form.formFields.zipcode.label') }}
        <form-required-symbol />
      </form-label>
      <input
        v-model="v$.postcode.$model"
        id="zipcode"
        name="zipcode"
        autocomplete="postal-code"
        class="form-control form-control-lg"
        :class="{
          'is-valid': !v$.postcode.$error && v$.postcode.$dirty,
          'is-invalid': v$.postcode.$error && v$.postcode.$dirty,
        }"
        :placeholder="$t('form.formFields.zipcode.placeholder')"
        data-testid="zipcode-input"
        @blur="handleResetExternalError('postcode')"
      />
      <form-field-error
        v-if="v$.postcode.$error"
        :errors="v$.postcode.$errors"
      ></form-field-error>
    </form-field-group>
  </div>
</template>

<style lang="scss" scoped>
@media (min-width: 992px) {
  .w-lg-75 {
    max-width: 75%;
  }
}
</style>
